import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "../api";
import { PageHeader, EmptyState, Modal, Field, Button } from "../components/UI";
import { Users as UsersIcon, Plus, Pencil, Trash2, Building2, GraduationCap, Printer, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const typeIcons = {
  hospital: Building2,
  school: GraduationCap,
  printing_press: Printer,
  other: UsersIcon,
};
const typeLabels = {
  hospital: "Hospital",
  school: "School",
  printing_press: "Printing Press",
  other: "Other",
};

const blankForm = {
  name: "", type: "hospital", tier: "normal", contact_person: "", phone: "",
  email: "", address: "", city: "", gstin: "", notes: "",
};

export default function Customers() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;
  const debTimer = useRef(null);

  const load = async (q = "", p = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(p * PAGE_SIZE) });
      if (q) params.set("q", q);
      const [r, c] = await Promise.all([
        api.get(`/customers?${params}`),
        api.get(`/customers/count${q ? `?q=${encodeURIComponent(q)}` : ""}`),
      ]);
      setList(r.data);
      setTotal(c.data.count);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load("", 0); }, []);

  // Debounced search
  useEffect(() => {
    if (debTimer.current) clearTimeout(debTimer.current);
    debTimer.current = setTimeout(() => {
      setPage(0);
      load(search.trim(), 0);
    }, 350);
    return () => debTimer.current && clearTimeout(debTimer.current);
    // eslint-disable-next-line
  }, [search]);

  const goPage = (p) => { setPage(p); load(search.trim(), p); };

  const openCreate = () => { setEditing(null); setForm(blankForm); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...blankForm, ...c }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer added");
      }
      setOpen(false);
      load(search.trim(), page);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer deleted");
      load(search.trim(), page);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = filter === "all" ? list : list.filter((c) => c.type === filter);

  return (
    <div className="p-4 lg:p-8 max-w-7xl">
      <PageHeader
        title="Customers"
        subtitle="Hospitals, schools, and small printing presses"
        action={
          <Button onClick={openCreate} data-testid="add-customer-btn">
            <Plus size={16} /> Add Customer
          </Button>
        }
      />

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-md" data-testid="customer-search-wrap">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none"/>
          <input
            type="text"
            data-testid="customer-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, city…"
            className="input-base pl-7"
          />
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="customer-filters">
          {["all", "hospital", "school", "printing_press", "other"].map((t) => (
            <button
              key={t}
              data-testid={`filter-${t}`}
              onClick={() => setFilter(t)}
              className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold transition ${
                filter === t ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569] hover:border-[#C1121F]"
              }`}
            >
              {t === "all" ? "All" : typeLabels[t]}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-[#475569] font-mono-num" data-testid="customer-count">
          {total.toLocaleString("en-IN")} total{search ? ` (matching "${search}")` : ""}
        </div>
      </div>

      {loading ? (
        <div className="text-[#475569]">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={UsersIcon}
          title="No customers yet"
          hint="Add hospitals, schools, or printing presses you serve to start tracking their orders."
          action={<Button onClick={openCreate}><Plus size={16}/> Add your first customer</Button>}
        />
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="crm-table" data-testid="customers-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Contact</th>
                <th>Phone</th>
                <th>City</th>
                <th>GSTIN</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const Icon = typeIcons[c.type] || UsersIcon;
                const tierCls = c.tier === "premium" ? "bg-purple-100 text-purple-800" : c.tier === "low" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-700";
                return (
                  <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                    <td>
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded bg-[#FEF2F2] flex items-center justify-center">
                          <Icon size={15} className="text-[#C1121F]" />
                        </div>
                        <div>
                          <div className="font-semibold">{c.name}</div>
                          {c.tier && c.tier !== "normal" && (
                            <span className={`inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${tierCls}`}>
                              {c.tier}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="text-[#475569]">{typeLabels[c.type]}</td>
                    <td>{c.contact_person || "—"}</td>
                    <td className="font-mono-num">{c.phone || "—"}</td>
                    <td>{c.city || "—"}</td>
                    <td className="font-mono-num text-xs">{c.gstin || "—"}</td>
                    <td className="text-right">
                      <button onClick={() => openEdit(c)} data-testid={`edit-${c.id}`} className="p-1.5 text-[#475569] hover:text-[#C1121F]"><Pencil size={15}/></button>
                      <button onClick={() => remove(c)} data-testid={`delete-${c.id}`} className="p-1.5 text-[#475569] hover:text-red-600"><Trash2 size={15}/></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4" data-testid="pagination">
          <div className="text-xs text-[#475569] font-mono-num">
            Page {page + 1} of {Math.ceil(total / PAGE_SIZE)} · Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)}
          </div>
          <div className="flex gap-2">
            <button onClick={() => goPage(Math.max(0, page - 1))} disabled={page === 0} data-testid="prev-page" className="px-3 py-1.5 text-xs font-semibold bg-white border border-[#E5E7EB] rounded hover:border-[#C1121F] disabled:opacity-40 flex items-center gap-1">
              <ChevronLeft size={12}/> Prev
            </button>
            <button onClick={() => goPage(Math.min(Math.ceil(total / PAGE_SIZE) - 1, page + 1))} disabled={(page + 1) * PAGE_SIZE >= total} data-testid="next-page" className="px-3 py-1.5 text-xs font-semibold bg-white border border-[#E5E7EB] rounded hover:border-[#C1121F] disabled:opacity-40 flex items-center gap-1">
              Next <ChevronRight size={12}/>
            </button>
          </div>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Customer" : "Add Customer"} maxWidth="max-w-2xl">
        <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="customer-form">
          <Field label="Name" required>
            <input data-testid="cust-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="input-base" />
          </Field>
          <Field label="Type" required>
            <select data-testid="cust-type" value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="input-base">
              <option value="hospital">Hospital</option>
              <option value="school">School</option>
              <option value="printing_press">Printing Press</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Tier">
            <select data-testid="cust-tier" value={form.tier} onChange={(e) => setForm({...form, tier: e.target.value})} className="input-base">
              <option value="premium">⭐ Premium</option>
              <option value="normal">Normal</option>
              <option value="low">⚠️ Low Priority</option>
            </select>
          </Field>
          <Field label="Contact Person">
            <input data-testid="cust-contact" value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} className="input-base" />
          </Field>
          <Field label="Phone">
            <input data-testid="cust-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="input-base" />
          </Field>
          <Field label="Email">
            <input data-testid="cust-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="input-base" />
          </Field>
          <Field label="City">
            <input data-testid="cust-city" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className="input-base" />
          </Field>
          <div className="col-span-2">
            <Field label="Address">
              <input data-testid="cust-address" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="input-base" />
            </Field>
          </div>
          <Field label="GSTIN">
            <input data-testid="cust-gstin" value={form.gstin} onChange={(e) => setForm({...form, gstin: e.target.value})} className="input-base" />
          </Field>
          <div className="col-span-2">
            <Field label="Notes">
              <textarea data-testid="cust-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="input-base" rows={2} />
            </Field>
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-customer-btn">{editing ? "Save Changes" : "Add Customer"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
